import React, { useState, useEffect } from 'react';
import { Calculator, TrendingUp, AlertCircle, CheckCircle, Info, ChevronDown } from 'lucide-react';
import { toast } from 'sonner';
import ApplyToAllServicesModal from './ApplyToAllServicesModal';

/**
 * Reader Cost Recovery Calculator
 * Calculates what rate increase is needed to cover reader costs
 * 
 * Formula: Rate Increase = Total Reader Cost / Annual Transaction Volume
 * 
 * Example:
 * - 10 readers × $300/each = $3,000
 * - Annual transaction volume: $1,000,000
 * - Rate increase needed: $3,000 / $1,000,000 = 0.003 = 0.30%
 * - If current rate is 2.00%, suggest 2.30%
 */
export default function ReaderCostRecoveryTool({
  scenarioInfo,
  volumeAssumptions,
  proposedPricing,
  readerTypesWithCosts,
  proposalScenarios = [],
  onApplySuggestion,
  readerCostRecoveryApplied = false,  // Check if already applied
  onApplyToAllServices  // NEW: Apply to all services in proposal
}) {
  const [calculation, setCalculation] = useState(null);
  const [isOpen, setIsOpen] = useState(false);
  const [appliedTemporarily, setAppliedTemporarily] = useState(false);
  const [showApplyAllModal, setShowApplyAllModal] = useState(false);
  const [applyingToAll, setApplyingToAll] = useState(false);

  useEffect(() => {
    calculateRecovery();
  }, [
    scenarioInfo.readerType,
    scenarioInfo.readersQty,
    volumeAssumptions.totalPayments,
    volumeAssumptions.ccPercent,
    volumeAssumptions.amexPercent,
    volumeAssumptions.debitPercent,
    proposalScenarios.length,
    proposedPricing.paymentRates.CC?.takeRate
  ]);

  // Auto-dismiss popover after 30 seconds when opened
  useEffect(() => {
    if (isOpen) {
      const timer = setTimeout(() => {
        setIsOpen(false);
      }, 30000); // 30 seconds

      return () => clearTimeout(timer);
    }
  }, [isOpen]);

  const calculateRecovery = () => {
    // Get reader cost for current service
    const readerType = readerTypesWithCosts.find(rt => rt.name === scenarioInfo.readerType);
    
    if (!readerType) {
      setCalculation(null);
      return;
    }
    
    // Check if this is a Dynamag or Sureswipe reader (high-cost readers requiring cross-service aggregation)
    const requiresAggregation = scenarioInfo.readerType === 'Dynamag' || scenarioInfo.readerType === 'Sureswipe';
    
    // Aggregate reader costs across ALL services in the proposal if Dynamag/Sureswipe
    let totalProposalReaderCost = 0;
    let totalProposalCardPayments = 0;
    let serviceBreakdown = [];
    
    if (requiresAggregation && proposalScenarios && proposalScenarios.length > 0) {
      // Aggregate across all services in proposal
      proposalScenarios.forEach(scenario => {
        const scenInfo = scenario.scenario_info || {};
        const volAssumptions = scenario.volume_assumptions || {};
        
        // Check if this service has Dynamag or Sureswipe readers
        if ((scenInfo.readerType === 'Dynamag' || scenInfo.readerType === 'Sureswipe') && scenInfo.readersQty > 0) {
          const serviceReaderType = readerTypesWithCosts.find(rt => rt.name === scenInfo.readerType);
          // Use monthly_cost * 12 for annual cost; fallback to cost_per_unit if monthly_cost not available
          const serviceMonthlyUnitCost = serviceReaderType?.monthly_cost || serviceReaderType?.cost_per_unit || 0;
          const serviceCost = serviceMonthlyUnitCost * 12 * (scenInfo.readersQty || 0);
          totalProposalReaderCost += serviceCost;
          
          // Calculate this service's card payment volume
          const servicePayments = (volAssumptions.totalPayments || 0) * 1000;
          const ccPct = (volAssumptions.ccPercent || 0) / 100;
          const amexPct = (volAssumptions.amexPercent || 0) / 100;
          const debitPct = (volAssumptions.debitPercent || 0) / 100;
          const cardPct = ccPct + amexPct + debitPct;
          const serviceCardPayments = servicePayments * cardPct;
          
          totalProposalCardPayments += serviceCardPayments;
          
          serviceBreakdown.push({
            name: scenInfo.service || 'Unknown Service',
            readerType: scenInfo.readerType,
            readerQty: scenInfo.readersQty,
            readerCost: serviceCost,
            cardPayments: serviceCardPayments
          });
        }
      });
    }
    
    // If no aggregation needed or no other services, use current service only
    if (!requiresAggregation || totalProposalReaderCost === 0) {
      if (!scenarioInfo.readersQty || scenarioInfo.readersQty === 0) {
        setCalculation(null);
        return;
      }
      
      // Use monthly_cost * 12 for annual cost; fallback to cost_per_unit if monthly_cost not set
      const readerMonthlyUnitCost = readerType.monthly_cost || readerType.cost_per_unit || 0;
      const readerQty = scenarioInfo.readersQty || 0;
      totalProposalReaderCost = readerMonthlyUnitCost * 12 * readerQty;  // Annualized
      
      // Calculate current service's card payment volume
      const totalPayments = (volumeAssumptions.totalPayments || 0) * 1000;
      const ccPercent = (volumeAssumptions.ccPercent || 0) / 100;
      const amexPercent = (volumeAssumptions.amexPercent || 0) / 100;
      const debitPercent = (volumeAssumptions.debitPercent || 0) / 100;
      const cardOnlyPercent = ccPercent + amexPercent + debitPercent;
      totalProposalCardPayments = totalPayments * cardOnlyPercent;
      
      serviceBreakdown = [{
        name: scenarioInfo.service || 'Current Service',
        readerType: scenarioInfo.readerType,
        readerQty: scenarioInfo.readersQty,
        readerCost: totalProposalReaderCost,
        cardPayments: totalProposalCardPayments
      }];
    }

    if (totalProposalCardPayments === 0) {
      setCalculation(null);
      return;
    }

    // Calculate rate increase needed (in percentage points) based on TOTAL proposal reader cost
    const rateIncreasePoints = (totalProposalReaderCost / totalProposalCardPayments) * 100;

    // Calculate current service's card payment volume for display
    const currentServicePayments = (volumeAssumptions.totalPayments || 0) * 1000;
    const currentCcPct = (volumeAssumptions.ccPercent || 0) / 100;
    const currentAmexPct = (volumeAssumptions.amexPercent || 0) / 100;
    const currentDebitPct = (volumeAssumptions.debitPercent || 0) / 100;
    const currentCardPct = currentCcPct + currentAmexPct + currentDebitPct;
    const currentServiceCardPayments = currentServicePayments * currentCardPct;

    // Calculate per-service breakdown for display (annualized: monthly_cost * 12 * qty)
    const readerAnnualUnitCost = (readerType.monthly_cost || readerType.cost_per_unit || 0) * 12;
    const currentService = {
      name: scenarioInfo.service || 'Current Service',
      readerCost: readerAnnualUnitCost * (scenarioInfo.readersQty || 0),
      totalReaderCost: readerAnnualUnitCost * (scenarioInfo.readersQty || 0),
      annualPayments: currentServiceCardPayments,
      rateIncreaseNeeded: rateIncreasePoints,
      currentRate: proposedPricing.paymentRates.CC?.takeRate || 0,
      suggestedRate: (proposedPricing.paymentRates.CC?.takeRate || 0) + rateIncreasePoints
    };

    setCalculation({
      totalReaderCost: totalProposalReaderCost,
      totalCardPayments: totalProposalCardPayments,
      rateIncreaseNeeded: rateIncreasePoints,
      currentService,
      serviceBreakdown,
      multiServiceAggregation: requiresAggregation && serviceBreakdown.length > 1
    });
  };

  const handleApplySuggestion = () => {
    if (!calculation || !onApplySuggestion) return;
    
    // Prevent double-application
    if (readerCostRecoveryApplied) {
      toast.warning('⚠️ Reader cost recovery already applied to this service');
      return;
    }

    // Calculate the suggested rates for all payment types
    const rateIncrease = calculation.overall?.rateIncrease || calculation.currentService.rateIncreaseNeeded;
    const currentRates = {
      CC: calculation.currentService.currentRate,
      AMEX: proposedPricing.paymentRates.AMEX?.takeRate || 0,
      DEBIT: proposedPricing.paymentRates.DEBIT?.takeRate || 0
    };
    
    const suggestedRates = {
      CC: currentRates.CC + rateIncrease,
      AMEX: currentRates.AMEX + rateIncrease,
      DEBIT: currentRates.DEBIT + rateIncrease
    };
    
    onApplySuggestion(suggestedRates, rateIncrease);
    setAppliedTemporarily(true);

    // Reset the applied flag after 3 seconds
    setTimeout(() => setAppliedTemporarily(false), 3000);
  };
  
  const handleApplyToAll = async () => {
    if (!calculation || !onApplyToAllServices) return;
    
    // Prevent if already applied
    if (readerCostRecoveryApplied) {
      toast.warning('⚠️ Reader cost recovery already applied to this service');
      return;
    }
    
    // Find services that need updating
    const servicesToUpdate = proposalScenarios.filter(scenario => {
      const scenInfo = scenario.scenario_info || {};
      const hasHighCostReaders = (scenInfo.readerType === 'Dynamag' || scenInfo.readerType === 'Sureswipe') && scenInfo.readersQty > 0;
      const recoveryApplied = scenario.reader_cost_recovery?.applied || false;
      return hasHighCostReaders && !recoveryApplied;
    });
    
    if (servicesToUpdate.length === 0) {
      toast.info('💡 All services already have reader cost recovery applied');
      return;
    }
    
    // Show preview modal
    setShowApplyAllModal(true);
  };
  
  const handleConfirmApplyToAll = async () => {
    if (!calculation || !onApplyToAllServices) return;
    
    const rateIncrease = calculation.overall?.rateIncrease || calculation.currentService.rateIncreaseNeeded;
    
    setApplyingToAll(true);
    try {
      await onApplyToAllServices(rateIncrease);
      setShowApplyAllModal(false);
      setAppliedTemporarily(true);
      setTimeout(() => setAppliedTemporarily(false), 3000);
    } catch (error) {
      console.error('Error applying to all:', error);
    } finally {
      setApplyingToAll(false);
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value || 0);
  };

  const formatPercent = (value, decimals = 2) => {
    return `${(value || 0).toFixed(decimals)}%`;
  };

  // Convert percentage points to basis points
  // 1% = 100 bps, 0.01% = 1 bp
  const formatBasisPoints = (percentValue) => {
    const bps = Math.round((percentValue || 0) * 100);
    return `${bps} bps`;
  };

  // Show a message if readers are configured but volume data is missing
  if (!calculation) {
    // Only show if readers are actually configured
    if (scenarioInfo.readersQty > 0 && scenarioInfo.readerType && scenarioInfo.readerType !== 'N/A') {
      return (
        <div className="p-4 bg-yellow-50 border-l-4 border-yellow-400 rounded-lg">
          <div className="flex items-center gap-2">
            <AlertCircle className="w-5 h-5 text-yellow-600" />
            <div>
              <h3 className="text-sm font-semibold text-yellow-900">💡 Reader Cost Recovery Calculator</h3>
              <p className="text-xs text-yellow-800 mt-1">
                Fill in <strong>Total Payments</strong> and the payment type percentages (<strong>CC%, AMEX%, DEBIT%</strong>) in the <strong>Volume Assumptions</strong> section to see rate recovery suggestions.
              </p>
            </div>
          </div>
        </div>
      );
    }
    return null;
  }

  return (
    <div className="relative">
      {/* Prominent Badge Button */}
      <button
        type="button"
        data-testid="reader-cost-recovery-toggle"
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center justify-between p-4 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white rounded-lg shadow-lg hover:shadow-xl transition-all border-2 border-blue-400"
      >
        <div className="flex items-center gap-3">
          <div className="p-2 bg-white/20 rounded-lg">
            <Calculator className="w-6 h-6" />
          </div>
          <div className="text-left">
            <h3 className="font-bold text-lg">💡 Reader Cost Recovery Calculator</h3>
            <p className="text-sm text-blue-100">
              {calculation && calculation.currentService
                ? `Suggest +${formatBasisPoints(calculation.currentService.rateIncreaseNeeded)} to cover ${formatCurrency(calculation.currentService.totalReaderCost)} in reader costs`
                : 'Calculate rate increase to cover reader hardware costs'
              }
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {calculation && (
            <span className="px-3 py-1 bg-yellow-400 text-yellow-900 font-bold rounded-full text-sm animate-pulse">
              Click to View
            </span>
          )}
          <ChevronDown className={`w-5 h-5 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
        </div>
      </button>

      {/* Popover / Data Card - Auto-dismisses after 30 seconds */}
      {isOpen && (
        <div data-testid="reader-cost-recovery-popover" className="absolute left-0 top-full mt-2 w-[600px] bg-white rounded-xl shadow-xl border-2 border-blue-200 z-40 p-5">
          {/* Header */}
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <Calculator className="w-5 h-5 text-blue-600" />
              <h3 className="text-lg font-bold text-slate-900">Reader Cost Recovery Calculator</h3>
            </div>
            <button
              type="button"
              onClick={() => setIsOpen(false)}
              className="text-slate-400 hover:text-slate-600"
            >
              ✕
            </button>
          </div>

          {/* Info Note - Enhanced Guidance */}
          <div className="flex items-start gap-2 bg-amber-50 border-l-4 border-amber-400 rounded-lg p-3 mb-4">
            <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
            <div className="text-xs text-amber-900">
              <p className="font-bold mb-1">⚠️ Important: Complete All Input Fields First</p>
              <p className="mb-2">
                This calculator becomes MORE ACCURATE as you fill out <strong>all relevant fields</strong> in the calculator:
              </p>
              <ul className="list-disc ml-4 space-y-1 mb-2">
                <li><strong>Total Payments</strong> and <strong>Total Transactions</strong> (Block 2)</li>
                <li><strong>Payment Type Percentages</strong> - CC%, AMEX%, DEBIT%, ACH% (Block 2)</li>
                <li><strong>Reader Quantity</strong> and <strong>Reader Type</strong> (Block 1)</li>
                <li><strong>Current Rates</strong> for all payment types (Block 4)</li>
              </ul>
              <p className="font-bold text-amber-800">
                💡 DO NOT accept the first suggestion you see. Fill out all fields, then review the final calculation for the most accurate rate recovery recommendation.
              </p>
              <p className="text-xs mt-2 text-amber-700">
                The "Apply Suggestion" button provides a what-if preview only—rates must be manually saved.
              </p>
            </div>
          </div>

          {/* Current Service Breakdown */}
          <div className="bg-slate-50 rounded-lg p-4 mb-4">
            <h4 className="text-sm font-semibold text-slate-700 mb-3 flex items-center gap-2">
              <TrendingUp className="w-4 h-4" />
              Current Service: {calculation.currentService.name}
            </h4>
            <div className="grid grid-cols-3 gap-3">
              <div className="bg-white rounded-lg p-3 border border-slate-200">
                <p className="text-xs text-slate-500 uppercase tracking-wide">Reader Cost</p>
                <p className="text-lg font-bold text-slate-900 mt-1">
                  {formatCurrency(calculation.currentService.readerCost)}
                </p>
              </div>
              <div className="bg-white rounded-lg p-3 border border-slate-200">
                <p className="text-xs text-slate-500 uppercase tracking-wide">Annual Card Volume</p>
                <p className="text-lg font-bold text-slate-900 mt-1">
                  {formatCurrency(calculation.currentService.annualPayments)}
                </p>
              </div>
              <div className="bg-blue-50 rounded-lg p-3 border border-blue-200">
                <p className="text-xs text-blue-600 uppercase tracking-wide font-semibold">Rate Increase</p>
                <p className="text-xl font-bold text-blue-700 mt-1">
                  +{formatBasisPoints(calculation.currentService.rateIncreaseNeeded)}
                </p>
                <p className="text-xs text-blue-500 mt-1">
                  {formatPercent(calculation.currentService.currentRate)} → {formatPercent(calculation.currentService.suggestedRate)}
                </p>
              </div>
            </div>
          </div>

          {/* Multi-Service Aggregation Warning - Dynamag/Sureswipe */}
          {calculation.multiServiceAggregation && calculation.serviceBreakdown && calculation.serviceBreakdown.length > 1 && (
            <div data-testid="multi-service-aggregation-warning" className="bg-amber-50 border-2 border-amber-400 rounded-lg p-4 mb-4">
              <div className="flex items-start gap-2 mb-3">
                <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                <div>
                  <h4 className="text-sm font-bold text-amber-900 mb-1">
                    ⚠️ Multi-Service Reader Cost Distribution
                  </h4>
                  <p className="text-xs text-amber-800">
                    This proposal has <strong>{calculation.serviceBreakdown.length} services</strong> with Dynamag/Sureswipe readers. 
                    The rate increase is calculated based on <strong>TOTAL reader costs across ALL services</strong> to prevent loading all readers on one service.
                  </p>
                </div>
              </div>
              
              <div className="bg-white rounded-lg border border-amber-200 p-3">
                <h5 className="text-xs font-semibold text-slate-700 mb-2">Reader Distribution Across Services:</h5>
                <div className="space-y-2">
                  {calculation.serviceBreakdown.map((service, idx) => (
                    <div key={idx} className="flex items-center justify-between text-xs py-1 border-b border-slate-100 last:border-0">
                      <div className="flex-1">
                        <span className="font-semibold text-slate-900">{service.name}</span>
                        <span className="text-slate-500 ml-2">({service.readerType})</span>
                      </div>
                      <div className="flex gap-4 text-right">
                        <div>
                          <span className="text-slate-500">Readers: </span>
                          <span className="font-semibold text-slate-900">{service.readerQty}</span>
                        </div>
                        <div>
                          <span className="text-slate-500">Cost: </span>
                          <span className="font-semibold text-slate-900">{formatCurrency(service.readerCost)}</span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
                <div className="mt-3 pt-2 border-t-2 border-amber-300 flex items-center justify-between">
                  <span className="text-xs font-bold text-amber-900">TOTAL READER COST (All Services):</span>
                  <span className="text-sm font-bold text-amber-900">{formatCurrency(calculation.totalReaderCost)}</span>
                </div>
                <div className="mt-2 pt-2 border-t border-slate-200 flex items-center justify-between">
                  <span className="text-xs font-bold text-slate-700">Total Card Volume (All Services):</span>
                  <span className="text-sm font-bold text-slate-900">{formatCurrency(calculation.totalCardPayments)}</span>
                </div>
                <div className="mt-2 pt-2 border-t border-blue-300 bg-blue-50 -mx-3 -mb-3 px-3 py-2 rounded-b flex items-center justify-between">
                  <span className="text-xs font-bold text-blue-900">Required Rate Increase (All Services):</span>
                  <span className="text-base font-bold text-blue-700">+{formatBasisPoints(calculation.rateIncreaseNeeded)}</span>
                </div>
              </div>
            </div>
          )}

          {/* Overall Summary (if multi-service proposal) */}
          {calculation.overall && (
            <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-lg p-4 mb-4 border border-blue-200">
              <h4 className="text-sm font-semibold text-indigo-800 mb-3 flex items-center gap-2">
                <CheckCircle className="w-4 h-4" />
                Overall Summary ({calculation.overall.serviceCount} Services)
              </h4>
              <div className="grid grid-cols-3 gap-3">
                <div className="bg-white rounded-lg p-3">
                  <p className="text-xs text-slate-500 uppercase tracking-wide">Total Reader Costs</p>
                  <p className="text-lg font-bold text-slate-900 mt-1">
                    {formatCurrency(calculation.overall.totalReaderCosts)}
                  </p>
                </div>
                <div className="bg-white rounded-lg p-3">
                  <p className="text-xs text-slate-500 uppercase tracking-wide">Total Annual Volume</p>
                  <p className="text-lg font-bold text-slate-900 mt-1">
                    {formatCurrency(calculation.overall.totalAnnualPayments)}
                  </p>
                </div>
                <div className="bg-indigo-600 rounded-lg p-3">
                  <p className="text-xs text-white uppercase tracking-wide font-semibold">Overall Rate Increase</p>
                  <p className="text-2xl font-bold text-white mt-1">
                    +{formatBasisPoints(calculation.overall.rateIncrease)}
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Actions */}
          <div className="pt-4 border-t border-slate-200 space-y-3">
            <div className="flex items-start gap-2 text-xs text-slate-500">
              <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
              <span>
                Applying this suggestion will <strong>temporarily preview</strong> the adjusted rates. 
                You must manually save the calculation to persist changes.
              </span>
            </div>
            
            <div className="flex items-center gap-3">
              <button
                type="button"
                data-testid="apply-reader-cost-suggestion"
                onClick={handleApplySuggestion}
                disabled={appliedTemporarily || readerCostRecoveryApplied}
                className={`flex-1 flex items-center justify-center gap-2 px-4 py-2 rounded-lg font-medium transition-all ${
                  readerCostRecoveryApplied
                    ? 'bg-gray-200 text-gray-500 cursor-not-allowed'
                    : appliedTemporarily
                    ? 'bg-green-100 text-green-700 border border-green-300'
                    : 'bg-blue-600 text-white hover:bg-blue-700 shadow-md hover:shadow-lg'
                }`}
                title={readerCostRecoveryApplied ? 'Already applied to this service' : ''}
              >
                {readerCostRecoveryApplied ? (
                  <>
                    <CheckCircle className="w-4 h-4" />
                    Already Applied
                  </>
                ) : appliedTemporarily ? (
                  <>
                    <CheckCircle className="w-4 h-4" />
                    Applied!
                  </>
                ) : (
                  <>
                    <TrendingUp className="w-4 h-4" />
                    Apply to This Service
                  </>
                )}
              </button>
              
              {/* Apply to All Services button - only for multi-service proposals */}
              {calculation.multiServiceAggregation && onApplyToAllServices && proposalScenarios.length > 1 && (
                <button
                  type="button"
                  data-testid="apply-to-all-services"
                  onClick={handleApplyToAll}
                  disabled={appliedTemporarily || readerCostRecoveryApplied}
                  className={`flex-1 flex items-center justify-center gap-2 px-4 py-2 rounded-lg font-medium transition-all ${
                    readerCostRecoveryApplied
                      ? 'bg-gray-200 text-gray-500 cursor-not-allowed'
                      : 'bg-purple-600 text-white hover:bg-purple-700 shadow-md hover:shadow-lg'
                  }`}
                  title={readerCostRecoveryApplied ? 'Already applied' : 'Apply rate increase to ALL services with readers'}
                >
                  <span className="text-lg">⚡</span>
                  Apply to All Services
                </button>
              )}
            </div>
          </div>
        </div>
      )}
      
      {/* Apply to All Services Modal */}
      <ApplyToAllServicesModal
        isOpen={showApplyAllModal}
        onClose={() => setShowApplyAllModal(false)}
        onConfirm={handleConfirmApplyToAll}
        servicesToUpdate={proposalScenarios.filter(scenario => {
          const scenInfo = scenario.scenario_info || {};
          const hasHighCostReaders = (scenInfo.readerType === 'Dynamag' || scenInfo.readerType === 'Sureswipe') && scenInfo.readersQty > 0;
          const recoveryApplied = scenario.reader_cost_recovery?.applied || false;
          return hasHighCostReaders && !recoveryApplied;
        })}
        rateIncrease={calculation?.overall?.rateIncrease || calculation?.currentService?.rateIncreaseNeeded || 0}
        loading={applyingToAll}
      />
    </div>
  );
}
